import React from "react";
import {
  Box,
  Typography,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Paper,
  TextField,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WeddingWizardProvider, {
  useWeddingWizard,
} from "../../context/WeddingWizardContext";
import WeddingPaymentForm from "../../components/WeddingPaymentForm";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import api from "../../services/api";

import WizardProgressHeader from "../../components/WizardProgressHeader";
import WizardFooter from "../../components/WizardFooter";

// Steps
import GuestConfigStep from "./steps/GuestConfigStep";
import ConfigSuggestionsStep from "./steps/ConfigSuggestionsStep";
import StylePickerStep from "./steps/StyleStep";
import FlavorsStep from "./steps/FlavorsStep";
import DessertsStep from "./steps/DessertsStep";
import RentalsStep from "./steps/RentalsStep";
import EventDateStep from "./steps/EventDateStep";
import DeliverySetupStep from "./steps/DeliverySetupStep";
import CustomerInfoStep from "./steps/CustomerInfoStep";
import ReviewStep from "./steps/ReviewStep";

const stripePromise = loadStripe("pk_test_szwkzwxsYnEZ6uzwIwy9cAK400C31iFiLA");

// Wizard step titles (order updated)
const steps = [
  "Basic Info",
  "Configuration",
  "Style",
  "Flavors",
  "Desserts",
  "Rentals",
  "Event Date",
  "Delivery & Setup",
  "Customer Info",
  "Review",
];

function StepBody() {
  const { state } = useWeddingWizard();
  switch (state.step) {
    case 0:
      return <GuestConfigStep />;
    case 1:
      return <ConfigSuggestionsStep />;
    case 2:
      return <StylePickerStep />;
    case 3:
      return <FlavorsStep />;
    case 4:
      return <DessertsStep />;
    case 5:
      return <RentalsStep />;
    case 6:
      return <EventDateStep />; // NEW
    case 7:
      return <DeliverySetupStep />;
    case 8:
      return <CustomerInfoStep />; // NEW
    case 9:
      return <ReviewStep />;
    default:
      return null;
  }
}

const makeIdemKey = () =>
  "wedd_draft_" + Math.random().toString(36).slice(2) + Date.now();

function WizardScaffold() {
  const {
    state,
    back,
    next,
    setPaymentInfo,
    setWeddingOrderId,
    buildOrderPayload,
  } = useWeddingWizard();

  const isReviewStep = state.step === steps.length - 1;

  // ----------------------------
  // Gating per step
  // ----------------------------

  // Step 0: guests hint — allow next as long as it's a valid number (>= 0)
  const canNextFromStep0 =
    Number.isFinite(Number(state?.planner?.guestsForSizing)) &&
    Number(state?.planner?.guestsForSizing) >= 0;

  // Step 1: configuration — require a compositeKey when cake is included
  const canNextFromStep1 = state.cake?.include
    ? !!state.cake?.compositeKey
    : true;

  // Step 2: style — require styleId when cake is included
  const canNextFromStep2 = state.cake?.include ? !!state.cake?.styleId : true;

  // Step 3: flavors — when cake is included, require tiers with flavorIds
  const tiers = Array.isArray(state.cake?.tiers) ? state.cake.tiers : [];
  const canNextFromStep3 = state.cake?.include
    ? tiers.length > 0 && tiers.every((t) => !!t.flavorId)
    : true;

  // Step 4: desserts — if included, require at least one valid selection
  const dessertSelections = Array.isArray(state.desserts?.selections)
    ? state.desserts.selections
    : [];
  const dessertsValid =
    dessertSelections.length === 0
      ? false
      : dessertSelections.every(
          (d) => d?.patisserieId && Number(d?.quantity) > 0
        );
  const canNextFromStep4 = state.desserts?.include ? dessertsValid : true;

  // Step 5: rentals — if included, require items with dates & quantity >= 1
  const rentalItems = Array.isArray(state.rentals?.items)
    ? state.rentals.items
    : [];
  const rentalsValid =
    rentalItems.length === 0
      ? false
      : rentalItems.every(
          (r) =>
            r?.productId &&
            r?.variantId &&
            r?.startDate &&
            r?.endDate &&
            Number(r?.quantity) >= 1
        );
  const canNextFromStep5 = state.rentals?.include ? rentalsValid : true;

  // Step 6: event date — require a selected date
  const canNextFromStep6 = !!state.fulfillment?.scheduledDate;

  // Step 7: delivery & setup — for delivery ensure address distance is numeric; pickup ok if location chosen
  const isDelivery = state.fulfillment?.method === "delivery";
  const dist = Number(state.fulfillment?.address?.distanceKm);
  const pickupLoc = state.fulfillment?.pickupLocation;
  const waiverAck = !!state.fulfillment?.waiver?.acknowledged;
  const tierCount = Array.isArray(state.cake?.tiers)
    ? state.cake.tiers.length
    : (String(state.cake?.compositeKey || "").match(/^(\d+)T/i)?.[1] &&
        Number(RegExp.$1)) ||
      0;
  const isThreePlus = tierCount >= 3;

  const canNextFromStep7 = isDelivery
    ? Number.isFinite(dist) // we’re lenient; address selection logic lives in step
    : !!pickupLoc && (!isThreePlus || waiverAck);

  // Step 8: customer info — require first, last, valid email
  const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneOk = (v) => {
    const s = String(v || "").trim();
    if (!s) return false;
    const digits = s.replace(/[^\d]/g, "");
    return digits.length >= 7 && digits.length <= 20;
  };
  const c = state.customer || {};
  const canNextFromStep8 =
    !!c.firstName?.trim() &&
    !!c.lastName?.trim() &&
    emailRe.test(String(c.email || "").trim()) &&
    phoneOk(c.phone);

  // Step 9 (review): compile all
  const canSubmitReview =
    canNextFromStep0 &&
    canNextFromStep1 &&
    canNextFromStep2 &&
    canNextFromStep3 &&
    canNextFromStep4 &&
    canNextFromStep5 &&
    canNextFromStep6 &&
    canNextFromStep7 &&
    canNextFromStep8;

  const payNowChoice = state.payment?.preference?.payNow ?? null; // 'full' | 'deposit50' | null
  const canSubmitReviewWithPay = canSubmitReview && !!payNowChoice;

  const nextDisabled =
    state.step === 0
      ? !canNextFromStep0
      : state.step === 1
      ? !canNextFromStep1
      : state.step === 2
      ? !canNextFromStep2
      : state.step === 3
      ? !canNextFromStep3
      : state.step === 4
      ? !canNextFromStep4
      : state.step === 5
      ? !canNextFromStep5
      : state.step === 6
      ? !canNextFromStep6
      : state.step === 7
      ? !canNextFromStep7
      : state.step === 8
      ? !canNextFromStep8
      : state.step === 9
      ? !canSubmitReviewWithPay // was !canSubmitReview
      : false;

  // ----------------------------
  // Next handler
  // ----------------------------
  const handleNext = () => {
    if (isReviewStep) {
      beginWeddingCheckout();
    } else {
      next();
    }
  };

  // ----------------------------
  // Draft creation (server-authoritative)
  // ----------------------------
  const beginWeddingCheckout = async () => {
    const payload = buildOrderPayload();
    payload.paymentPreference = state.payment?.preference || null;
    if (!payload.paymentPreference?.payNow) {
      console.warn("[WeddingWizard] No payment preference selected.");
      return; // or surface a toast
    }
    const idemKey = makeIdemKey();

    try {
      const { data } = await api.post("/weddings/orders/draft", payload, {
        headers: {
          "Idempotency-Key": idemKey,
        },
      });

      if (!data?.ok) {
        console.error("[WeddingWizard] Draft not OK:", data?.error);
        return;
      }

      if (data?.orderId) {
        setWeddingOrderId(data.orderId);
      }

      if (data?.clientSecret) {
        setPaymentInfo("element", data.clientSecret);
        return;
      }

      console.warn("[WeddingWizard] Missing clientSecret in draft response.");
    } catch (err) {
      const msg =
        err?.response?.data?.error?.message ||
        err?.message ||
        "Network error. Please try again.";
      console.error(
        "[WeddingWizard] Draft create error:",
        msg,
        err?.response?.data
      );
    }
  };

  return (
    <Container
      maxWidth="lg"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: { xs: 1.5, md: 3 },
        mt: 5,
      }}>
      <Paper
        elevation={2}
        sx={{
          p: 0,
          borderRadius: 3,
          width: "100%",
          maxHeight: { xs: "95vh", md: "92vh" },
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}>
        {/* Sticky header */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 2,
            bgcolor: "background.paper",
            borderBottom: (t) => `1px solid ${t.palette.divider}`,
            px: { xs: 2.5, md: 3 },
            pt: { xs: 2, md: 2.5 },
            pb: { xs: 2, md: 2 },
          }}>
          <WizardProgressHeader
            title="Let’s get your order details set up."
            steps={steps}
            active={state.step}
            showTitle
          />
        </Box>

        {/* Scrollable content */}
        <Box
          sx={{
            flex: "1 1 auto",
            overflow: "auto",
            px: { xs: 2.5, md: 3 },
            pt: 0,
            pb: { xs: 2, md: 3 },
          }}>
          <StepBody />
        </Box>

        {/* Sticky footer */}
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            zIndex: 2,
            bgcolor: "background.paper",
            borderTop: (t) => `1px solid ${t.palette.divider}`,
            px: { xs: 2.5, md: 3 },
            pb: { xs: 2, md: 2.5 },
            pt: 2,
          }}>
          <WizardFooter
            backDisabled={state.step === 0}
            nextDisabled={nextDisabled}
            onBack={() => back()}
            onNext={handleNext}
            isReviewStep={isReviewStep}
            disabledHint={
              isReviewStep && !canSubmitReviewWithPay
                ? "Choose how you want to pay to enable this button."
                : null
            }
          />
        </Box>

        {/* Payment Element dialog */}
        <Dialog
          open={
            state.payment?.mode === "element" && !!state.payment?.clientSecret
          }
          onClose={() => setPaymentInfo(null, null)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}>
          <DialogTitle sx={{ pr: 6 }}>
            Secure Payment
            <IconButton
              aria-label="close"
              onClick={() => setPaymentInfo(null, null)}
              sx={{ position: "absolute", right: 8, top: 8 }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers sx={{ pt: 2, pb: 3 }}>
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret: state.payment?.clientSecret,
                appearance: { theme: "stripe" },
              }}>
              <WeddingPaymentForm
                onClose={() => setPaymentInfo(null, null)}
                onSuccess={() => {
                  if (state.weddingOrderId) {
                    window.location.assign(
                      `/weddings/confirmation?order=${state.weddingOrderId}`
                    );
                  } else {
                    setPaymentInfo(null, null);
                  }
                }}
              />
            </Elements>
          </DialogContent>
        </Dialog>
      </Paper>
    </Container>
  );
}

export default function WeddingWizardPage() {
  return (
    <WeddingWizardProvider>
      <WizardScaffold />
    </WeddingWizardProvider>
  );
}
